import { useStore } from '@nanostores/react';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { useAuth } from '@/services/auth';
import { billing } from '@/services/billing';
import { ProductApp } from '@/services/billing/plans';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { masqReadOnlyStore } from '@/utils/masq';
import type { PlanInfoModel } from '../types';

/**
 * Reads the billing service + account into the shared {@link PlanInfoModel} that every plan card
 * (available-plans grid, checkout summary) consumes. Subscribes to the billing atoms the getters
 * (`trialExpired`, `planSummary`, `daysLeftInTrial`, …) read so they re-evaluate on re-render,
 * matching the billing shell's "subscribe to the atoms, read the getters directly" pattern.
 */
// Assembles the whole view model from many billing atoms/getters; the breadth is inherent (same
// pattern and justification as the sibling `useYourPlanData`), not reducible nesting.
// fallow-ignore-next-line complexity
export function usePlanInfoModel(): PlanInfoModel {
  const { account } = useAuth();
  const { hasPermission } = usePermissions();

  const upgradePlanTypes = useStore(billing.upgradePlanTypes);
  const isSubscriptionActive = useStore(billing.isSubscriptionActive);
  const isPendingCheckout = useStore(billing.isPendingCheckout);
  const isPremiumTrial = useStore(billing.isPremiumTrial);
  const isBillingTrial = useStore(billing.isTrial);
  const currentPlanType = useStore(billing.planType);
  const futurePlanType = useStore(billing.futurePlanType);
  const futureSubscription = useStore(billing.futureSubscription);
  const isBuilder = useStore(billing.isBuilder);
  const masqReadOnly = useStore(masqReadOnlyStore);

  // Subscribe to the subscription atom so the time-relative getters below recompute on re-render.
  useStore(billing.subscription);

  const newPipelinesObjectsPricing = useFeatureFlag(AccountFeatureFlag.NewPipelinesObjectsPricing);

  return {
    nextUpgradePlanType: upgradePlanTypes[ProductApp.Journeys],
    hasActiveSubscription: isSubscriptionActive,
    isPendingCheckout,
    isPremiumTrial,
    isBillingTrial,
    trialExpired: billing.trialExpired,
    planSummary: billing.planSummary,
    currentPlanType,
    futurePlanType,
    hasFutureSubscription: futureSubscription != null,
    isBuilder,
    startDateInFuture: billing.startDateInFuture,
    newPipelinesObjectsPricing,
    isReadOnly: masqReadOnly && account.is_masq === true,
    daysLeftInTrial: billing.daysLeftInTrial,
    trialExpiresAt: billing.trialExpiresAt,
    canViewBilling: hasPermission('account/billing/view'),
  };
}
